import { useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { Settings, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface BearingHealthRow {
  machine_id: string;
  status: string;
  colour: string;
  summary_line: string;
  mitigating: string | null;
  confidence: string;
  has_pv_alarm: boolean;
  worst_maxpk_g: number | null;
  worst_maxpk_severity: string | null;
  calculated_at: string;
  machine_name: string;
  area_name: string;
  company_name: string;
  db_id: number;
}

const STATUS_ORDER: Record<string, number> = {
  Warning: 1, Caution: 2, Advisory: 3, Watch: 4, Normal: 5,
};

function fmtUtcToEastern(ts: string): string {
  if (!ts) return '—';
  let s = ts.trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ConfidencePip({ level }: { level: string }) {
  const colors = { high: '#16a34a', medium: '#d97706', low: '#94a3b8' };
  const color = colors[level as keyof typeof colors] ?? '#94a3b8';
  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: color }} />
      {level}
    </span>
  );
}

type SortKey = 'status' | 'machine_name' | 'area_name' | 'worst_maxpk_g' | 'confidence' | 'calculated_at';

interface Props {
  onMachineClick: (machineId: string, machineName: string, points: any[]) => void;
}

export function BearingHealth({ onMachineClick }: Props) {
  const [rows, setRows] = useState<BearingHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dbFilter, setDbFilter] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setLoading(true);
    apiFetch<BearingHealthRow[]>('/machines/bearing-health')
      .then(data => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const databases = Array.from(
    new Map(rows.map(r => [r.db_id, r.company_name || String(r.db_id)])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const STATUSES = ['all', 'Warning', 'Caution', 'Advisory', 'Watch', 'Normal'];

  const filtered = rows
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    .filter(r => dbFilter === null || r.db_id === dbFilter);

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'status') cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    else if (sortKey === 'machine_name') cmp = a.machine_name.localeCompare(b.machine_name);
    else if (sortKey === 'area_name') cmp = a.area_name.localeCompare(b.area_name);
    else if (sortKey === 'worst_maxpk_g') cmp = (b.worst_maxpk_g ?? 0) - (a.worst_maxpk_g ?? 0);
    else if (sortKey === 'confidence') {
      const confOrder: Record<string, number> = { high: 1, medium: 2, low: 3 };
      cmp = (confOrder[a.confidence] ?? 4) - (confOrder[b.confidence] ?? 4);
    }
    else if (sortKey === 'calculated_at') cmp = a.calculated_at.localeCompare(b.calculated_at);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortHeader = ({ col, label, className = '' }: { col: SortKey; label: string; className?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === col && (
          <span className="text-[#dc2626]">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );

  // Status distribution counts
  const counts = STATUSES.filter(s => s !== 'all').map(s => ({
    status: s,
    n: rows.filter(r => r.status === s).length,
    colour: rows.find(r => r.status === s)?.colour ?? '#94a3b8',
  }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <Settings className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Impact Score</h1>
            <p className="text-sm text-gray-500">Impact View + periodicity + spectral assessment — {rows.length} machines</p>
          </div>
        </div>

        {/* Status distribution pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          {counts.map(c => (
            <button
              key={c.status}
              onClick={() => setStatusFilter(statusFilter === c.status ? 'all' : c.status)}
              className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${
                statusFilter === c.status
                  ? 'text-white border-transparent'
                  : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
              }`}
              style={statusFilter === c.status ? { backgroundColor: c.colour, borderColor: c.colour } : {}}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusFilter === c.status ? 'white' : c.colour }} />
              {c.status} ({c.n})
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {databases.length > 1 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Database:</span>
            {databases.map(([id, name]) => (
              <button
                key={id}
                onClick={() => setDbFilter(dbFilter === id ? null : id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                  dbFilter === id
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        {(statusFilter !== 'all' || dbFilter !== null) && (
          <button
            onClick={() => { setStatusFilter('all'); setDbFilter(null); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{sorted.length} shown</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Settings className="w-8 h-8 opacity-40" />
            <p className="text-sm">No results for current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader col="machine_name" label="Machine" />
                  <SortHeader col="area_name" label="Area" />
                  <SortHeader col="status" label="Impact Status" />
                  <SortHeader col="worst_maxpk_g" label="MaxPk (G)" />
                  <SortHeader col="confidence" label="Confidence" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Summary</th>
                  <SortHeader col="calculated_at" label="Updated" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map(row => (
                  <tr
                    key={row.machine_id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onMachineClick(row.machine_id, row.machine_name, [])}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.machine_name}</div>
                      <div className="text-xs text-gray-400">{row.company_name}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{row.area_name}</td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: row.colour }}
                      >
                        {row.status === 'Normal'
                          ? '✓'
                          : <Settings className="w-2.5 h-2.5" />
                        }
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {row.worst_maxpk_g != null
                        ? <span className={`text-xs font-medium ${row.worst_maxpk_g >= 20 ? 'text-red-600' : row.worst_maxpk_g >= 10 ? 'text-orange-600' : row.worst_maxpk_g >= 5 ? 'text-yellow-600' : 'text-gray-600'}`}>
                            {row.worst_maxpk_g.toFixed(1)}G
                          </span>
                        : <span className="text-xs text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <ConfidencePip level={row.confidence} />
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {row.summary_line
                        ? <span className="text-xs text-gray-600 line-clamp-2">{row.summary_line}</span>
                        : <span className="text-xs text-gray-400">—</span>
                      }
                      {row.mitigating && (
                        <div className="text-xs text-green-600 mt-0.5">✓ {row.mitigating}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {fmtUtcToEastern(row.calculated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
